import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Award, House, Users, Phone, Check } from "lucide-react";
import PastWorkCarousel from "@/components/PastWorkCarousel";

const IMAGES = {
  mitigation: "https://customer-assets.emergentagent.com/job_calgary-radon/artifacts/6wswmxqh_IMG_7739.jpeg",
  measurement: "https://customer-assets.emergentagent.com/job_calgary-radon/artifacts/5dkeh09l_IMG_7575.jpeg",
};

export default function HomePage() {
  return (
    <main>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="relative pt-24 pb-8 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="hero-section">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-50 -z-10" />
          <div className="max-w-6xl mx-auto text-center">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100 text-sm px-3 py-0.5">
              Serving Calgary and Surrounding Areas
            </Badge>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3 leading-tight">
              Protect Your Home from<span className="text-blue-600"> Radon Gas</span>
            </h1>
            <p className="text-base md:text-lg text-gray-600 mb-5 leading-relaxed max-w-2xl mx-auto">
              Radon is invisible, odorless, and dangerous. The leading cause of lung cancer in non-smokers. Test your home today.
            </p>

            {/* Call Now Button */}
            <a href="tel:+14034046013" className="group inline-block mb-6" data-testid="call-now-btn">
              <div className="flex items-center gap-4 bg-green-600 hover:bg-green-700 text-white rounded-xl px-7 py-4 shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02]">
                <div className="bg-white/20 p-3 rounded-full group-hover:bg-white/30 transition-colors">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-green-100">Call Now - Free Consultation</p>
                  <p className="text-2xl font-bold tracking-wide">(403) 404-6013</p>
                </div>
              </div>
            </a>

            {/* Two Pricing Cards Side by Side */}
            <div className="grid grid-cols-2 gap-4 max-w-3xl mx-auto" data-testid="pricing-cards">
              {/* Free Testing Card */}
              <div className="bg-blue-600 rounded-xl p-5 shadow-xl text-white text-left">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-green-500 text-white hover:bg-green-500 text-xs px-2 py-0.5 border-0">FREE FOR ALL HOMEOWNERS</Badge>
                </div>
                <h3 className="text-lg font-bold mb-1">Free Radon Testing</h3>
                <p className="text-blue-100 text-sm mb-3">Professional testing — no strings attached.</p>
                <div className="bg-white/10 rounded-lg p-3 mb-4 text-center">
                  <span className="text-4xl font-bold">$0</span>
                  <p className="text-blue-200 text-xs mt-1">Zero cost &bull; Zero commitment</p>
                </div>
                <Link to="/book-test">
                  <Button className="w-full bg-white text-blue-600 hover:bg-blue-50 font-bold py-2.5 text-xs sm:text-sm shadow-md" data-testid="schedule-test-btn">
                    Book Free Test <ArrowRight className="ml-1 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </Button>
                </Link>
              </div>

              {/* Mitigation Card */}
              <div className="bg-white rounded-xl p-5 shadow-xl border-2 border-blue-200 text-left">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-blue-600 text-white hover:bg-blue-600 text-xs px-2 py-0.5 border-0">FROM $1,800</Badge>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Radon Mitigation System</h3>
                <p className="text-gray-500 text-sm mb-3">Expert installation to reduce radon to safe levels.</p>
                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-center">
                  <span className="text-4xl font-bold text-gray-900">$1,800</span>
                  <p className="text-gray-500 text-xs mt-1">Starting price &bull; Custom quote available</p>
                </div>
                <Link to="/get-quote">
                  <Button variant="outline" className="w-full border-blue-600 text-blue-600 hover:bg-blue-50 font-bold py-2.5 text-sm" data-testid="view-pricing-btn">
                    Get a Quote <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Facts */}
        <section className="bg-blue-600 py-6 px-4" data-testid="facts-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-white text-center mb-4">Calgary Radon Facts You Should Know</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center mb-3">
              {[
                { stat: "#1", desc: "Cause of lung cancer in non-smokers" },
                { stat: "1 in 6", desc: "Calgary homes have high radon levels" },
                { stat: "3,300", desc: "Radon-related deaths in Canada annually" },
              ].map((f) => (
                <div key={f.stat} className="animate-fade-in bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <p className="text-2xl font-bold text-white mb-1">{f.stat}</p>
                  <p className="text-blue-100 font-medium text-sm">{f.desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center">
              <p className="text-blue-100 text-sm font-medium">Testing is the ONLY way to know if your home is safe</p>
            </div>
          </div>
        </section>

        {/* Past Work Carousel */}
        <PastWorkCarousel />

        {/* Services */}
        <section className="py-12 px-4 sm:px-6 lg:px-8" data-testid="services-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Our Services</h2>
              <p className="text-base text-gray-600 max-w-2xl mx-auto">Complete radon solutions for your home</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <ServiceCard
                title="Radon Mitigation"
                image={IMAGES.mitigation}
                description="Professional installation of radon mitigation systems to reduce radon levels in your home to safe levels."
                features={["Guaranteed safe levels", "Top-notch workmanship", "Quality installations"]}
              />
              <ServiceCard
                title="Radon Measurement"
                image={IMAGES.measurement}
                description="Accurate radon testing using professional-grade equipment. Get detailed reports and expert recommendations."
              />
            </div>
          </div>
        </section>

        {/* Why Choose */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="why-choose-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Why Alberta Homeowners Choose ZARC Radon Solutions</h2>
              <p className="text-base text-gray-600">Local. Professional. Trusted.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: Award, title: "Local Calgary Experts", desc: "With 6+ years in the industry serving Calgary and area" },
                { icon: House, title: "Same-Day Service", desc: "Most systems installed in one day with minimal disruption to your daily life" },
                { icon: Users, title: "100% Satisfaction", desc: "We guarantee safe radon levels or your money back. No risk, just results" },
              ].map((item) => (
                <Card key={item.title} className="border-2 hover:border-blue-200 transition-all hover:shadow-xl">
                  <CardContent className="p-6 text-center">
                    <div className="bg-blue-600 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                      <item.icon className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-blue-700 overflow-hidden" data-testid="cta-section">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.2)_0%,transparent_50%)]" />
          </div>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to Make Your Home Safe?</h2>
            <p className="text-lg text-blue-100 mb-6 font-medium">Free testing &bull; Fast installation &bull; Guaranteed results</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/book-test">
                <Button className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-8 py-5 text-base shadow-lg" data-testid="cta-book-test-btn">
                  Book Free Test
                </Button>
              </Link>
              <Link to="/get-quote">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-blue-700 font-bold px-8 py-5 text-base" data-testid="cta-get-quote-btn">
                  Get a Quote
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

function ServiceCard({ title, image, description, features }) {
  return (
    <Card className="service-card overflow-hidden border-2 hover:border-blue-200">
      <div className="relative h-48 overflow-hidden">
        <img alt={title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-110" src={image} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <h3 className="absolute bottom-3 left-4 text-xl font-bold text-white">{title}</h3>
      </div>
      <CardContent className="p-4">
        <p className="text-gray-600 text-sm mb-4">{description}</p>
        {features && (
          <ul className="space-y-1.5 mb-4">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">{f}</span>
              </li>
            ))}
          </ul>
        )}
        <Link to="/services">
          <Button variant="outline" className="w-full border-blue-600 text-blue-600 hover:bg-blue-50 text-sm" data-testid={`learn-more-${title.toLowerCase().replace(/\s/g, '-')}-btn`}>
            Learn More <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
